import React from 'react';
import { Bot, AlertCircle, CheckCircle2 } from 'lucide-react';

const AIDeclarationForm = ({ formData, setFormData }) => {
  const aiUsage = formData.ai_usage_type || 'none';
  const selectedParts = formData.ai_parts_used || [];
  const tools = formData.ai_tools_used || '';
  const prompt = formData.ai_prompt_used || '';
  const otherSpec = formData.ai_other_specification || '';
  const declarationAccepted = formData.ai_declaration_accepted || false;

  const handleAiUsageChange = (value) => {
    setFormData({
      ...formData,
      ai_usage: value !== 'none',
      ai_usage_type: value,
      ai_parts_used: value === 'none' ? [] : formData.ai_parts_used || [],
      ai_tools_used: value === 'none' ? '' : formData.ai_tools_used || '',
      ai_prompt_used: value === 'none' ? '' : formData.ai_prompt_used || '',
      ai_other_specification: value === 'none' ? '' : formData.ai_other_specification || '',
      ai_declaration_accepted: value === 'none' ? false : formData.ai_declaration_accepted || false,
      ai_declaration_date: value === 'none' ? null : formData.ai_declaration_date
    });
  };

  const togglePart = (part) => {
    const newParts = selectedParts.includes(part)
      ? selectedParts.filter(p => p !== part)
      : [...selectedParts, part];
    
    setFormData({
      ...formData,
      ai_parts_used: newParts
    });
  };

  const updateTools = (value) => {
    setFormData({
      ...formData,
      ai_tools_used: value
    });
  };

  const updatePrompt = (value) => {
    setFormData({
      ...formData,
      ai_prompt_used: value
    });
  };

  const updateOtherSpec = (value) => {
    setFormData({
      ...formData,
      ai_other_specification: value
    });
  };

  const updateDeclaration = (checked) => {
    setFormData({
      ...formData,
      ai_declaration_accepted: checked,
      ai_declaration_date: checked ? new Date().toISOString() : null
    });
  };

  const aiParts = [
    { id: 'letra', label: 'Letra' },
    { id: 'melodia', label: 'Melodia' },
    { id: 'harmonia', label: 'Harmonia' },
    { id: 'arranjo', label: 'Arranjo Musical' },
    { id: 'partitura', label: 'Partitura / Notação Musical' },
    { id: 'audio_voz', label: 'Áudio / Voz (ex.: Suno, Udio, IA Vocal)' },
    { id: 'instrumental', label: 'Instrumental (ex.: bases geradas por IA)' },
    { id: 'fonograma_completo', label: 'Criação completa do fonograma' },
    { id: 'outro', label: 'Outro (especificar abaixo)' }
  ];

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl border-2 border-purple-200 p-6 shadow-lg">
      <div className="flex items-start gap-3 mb-4">
        <div className="bg-purple-100 p-3 rounded-lg">
          <Bot className="w-6 h-6 text-purple-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-gray-900 mb-1">
            Uso de Inteligência Artificial na Obra
          </h3>
          <p className="text-sm text-gray-600">
            Conformidade com ECAD 2025 e Lei 9.610/98
          </p>
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-800 mb-3">
          1. A obra teve participação de Inteligência Artificial Generativa (IAG)?
        </label>
        <div className="space-y-3">
          <div 
            onClick={() => handleAiUsageChange('none')}
            className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-green-400 cursor-pointer transition-all"
          >
            <input
              type="radio"
              name="ai_usage_radio"
              value="none"
              checked={aiUsage === 'none'}
              onChange={() => handleAiUsageChange('none')}
              className="w-5 h-5 text-green-600 cursor-pointer"
            />
            <span className="font-medium text-gray-800 flex-1">☐ Não, a obra é 100% humana.</span>
            {aiUsage === 'none' && <CheckCircle2 className="w-5 h-5 text-green-600" />}
          </div>

          <div 
            onClick={() => handleAiUsageChange('total')}
            className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-orange-400 cursor-pointer transition-all"
          >
            <input
              type="radio"
              name="ai_usage_radio"
              value="total"
              checked={aiUsage === 'total'}
              onChange={() => handleAiUsageChange('total')}
              className="w-5 h-5 text-orange-600 cursor-pointer"
            />
            <span className="font-medium text-gray-800 flex-1">☐ Sim, uso total de IA.</span>
            {aiUsage === 'total' && <CheckCircle2 className="w-5 h-5 text-orange-600" />}
          </div>

          <div 
            onClick={() => handleAiUsageChange('partial')}
            className="flex items-center gap-3 p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-yellow-400 cursor-pointer transition-all"
          >
            <input
              type="radio"
              name="ai_usage_radio"
              value="partial"
              checked={aiUsage === 'partial'}
              onChange={() => handleAiUsageChange('partial')}
              className="w-5 h-5 text-yellow-600 cursor-pointer"
            />
            <span className="font-medium text-gray-800 flex-1">☐ Sim, uso parcial de IA.</span>
            {aiUsage === 'partial' && <CheckCircle2 className="w-5 h-5 text-yellow-600" />}
          </div>
        </div>
      </div>

      {(aiUsage === 'partial' || aiUsage === 'total') && (
        <div className="space-y-6 animate-fade-in">
          <div className="border-t-2 border-purple-200 pt-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertCircle className="w-5 h-5 text-purple-600" />
              <h4 className="font-bold text-gray-900">Detalhamento do Uso de IA</h4>
            </div>

            {(aiUsage === 'partial' || aiUsage === 'total') && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  2. Qual parte da obra utilizou Inteligência Artificial? <span className="text-red-600">*</span>
                  <span className="text-xs text-gray-500 ml-2">(Obrigatório quando houver uso parcial ou total)</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {aiParts.map(part => (
                    <div 
                      key={part.id}
                      onClick={() => togglePart(part.id)}
                      className="flex items-start gap-2 p-3 bg-white rounded-lg border border-gray-200 hover:border-purple-400 cursor-pointer transition-all"
                    >
                      <input
                        type="checkbox"
                        checked={selectedParts.includes(part.id)}
                        onChange={() => togglePart(part.id)}
                        className="mt-1 w-4 h-4 text-purple-600 rounded cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{part.label}</span>
                    </div>
                  ))}
                </div>
                {selectedParts.includes('outro') && (
                  <input
                    type="text"
                    value={otherSpec}
                    onChange={(e) => updateOtherSpec(e.target.value)}
                    placeholder="Especifique qual outra parte..."
                    className="mt-3 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                )}
              </div>
            )}

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                3. Qual ferramenta ou plataforma foi utilizada? <span className="text-red-600">*</span>
              </label>
              <input
                type="text"
                value={tools}
                onChange={(e) => updateTools(e.target.value)}
                placeholder="Ex: Suno AI, Udio, ChatGPT, Gemini, Claude, AIVA, etc."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Digite o nome das ferramentas separadas por vírgula
              </p>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                4. (Opcional, mas recomendado) Informe o prompt/comando utilizado:
              </label>
              <textarea
                value={prompt}
                onChange={(e) => updatePrompt(e.target.value)}
                placeholder='Ex: "Crie uma letra de sofrência sertaneja em primeira pessoa" ou "Gere uma base de forró com sanfona suave"'
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Opcional, mas recomendado para documentação completa da obra
              </p>
            </div>

            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
              <div className="flex items-start gap-3 mb-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h5 className="font-bold text-gray-900 mb-2">5. Declaração Obrigatória</h5>
                  <p className="text-sm text-gray-700 leading-relaxed">
                    <strong>Declaro que as informações prestadas sobre o uso de Inteligência Artificial nesta obra são verdadeiras.</strong>
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed mt-2">
                    Estou ciente de que <strong>a autoria humana é exigência legal no Brasil (Lei 9.610/98)</strong>, e assumo total responsabilidade pela originalidade, licitude e conteúdo gerado, isentando a plataforma Minha Composição de qualquer responsabilidade por eventual disputa de autoria, plágio ou direitos conexos.
                  </p>
                </div>
              </div>
              
              <div 
                onClick={() => updateDeclaration(!declarationAccepted)}
                className="flex items-start gap-3 p-4 bg-white rounded-lg border-2 border-gray-300 hover:border-yellow-500 cursor-pointer transition-all mt-3"
              >
                <input
                  type="checkbox"
                  checked={declarationAccepted}
                  onChange={(e) => updateDeclaration(e.target.checked)}
                  required
                  className="mt-1 w-5 h-5 text-yellow-600 rounded cursor-pointer"
                />
                <span className="text-sm font-semibold text-gray-900">
                  ☐ Concordo e desejo continuar com o registro.
                </span>
              </div>
            </div>

            {aiUsage === 'total' && (
              <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>ℹ️ Aviso:</strong> Obras 100% criadas por IA não geram direito autoral patrimonial ou moral conforme Lei 9.610/98, mas podem ser registradas como obra técnica.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 pt-4 border-t border-purple-200">
        {aiUsage === 'none' ? (
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-medium">✓ Obra 100% humana declarada</span>
          </div>
        ) : declarationAccepted ? (
          <div className="flex items-center gap-2 text-green-700">
            <CheckCircle2 className="w-5 h-5" />
            <span className="text-sm font-medium">✓ Declaração de IA aceita e registrada</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-orange-600">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm font-medium">⚠ É necessário aceitar a declaração para prosseguir</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDeclarationForm;
